import Vue from 'vue';
import VueI18n from 'vue-i18n';
import axios from 'axios';
import { appUrl, customer } from '@/helpers/globals';
import dayjs from 'dayjs';
import 'dayjs/locale/de-ch';
import 'dayjs/locale/en-gb';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

Vue.use(VueI18n);

type LocaleStringMap = { [key: string]: string };

let i18nInstance: VueI18n | null = null;
const supportedLocales = ['de', 'en'];
const dayJsLocaleMap = {
    de: 'de-ch',
    en: 'en-gb',
} as LocaleStringMap;

/**
 * Loads the locale strings from locales/[locale].json. Returns a promise
 * which resolves the key/value strings for that locale.
 */
export function loadLocaleStrings(locale: string): Promise<LocaleStringMap> {
    if (supportedLocales.indexOf(locale) < 0) {
        locale = 'de'; // fallback locale
    }

    // load correct locale strings, then load customer specific strings, if any, then merge and return the result:
    return axios.get(appUrl(`locales/${locale}.json`)).then((res) => {
        let localeStrings = res.data || {};
        // load customer-specific strings. Ignore loading errors, as not all customers has specific string files.
        return new Promise((resolve) => {
            axios
                .get(appUrl(`locales/customers/${customer()}/${locale}.json`))

                .then((customerRes) => {
                    if (customerRes.data) {
                        localeStrings = Object.assign(localeStrings, customerRes.data);
                    }
                })
                .catch(() => {
                    // noop
                })
                .finally(() => {
                    resolve(localeStrings);
                });
        });
    });
}

function setI18nLanguage(i18nInstance: VueI18n, lang: string) {
    // set i18n locale
    i18nInstance.locale = lang;
    axios.defaults.headers.common['Accept-Language'] = lang;
    const doc = document.querySelector('html');
    if (doc) {
        doc.setAttribute('lang', lang);
    }

    // Configure dayjs locale:
    dayjs.locale(dayJsLocaleMap[lang] || 'de-ch');
    dayjs.extend(localizedFormat);
    dayjs.extend(customParseFormat);
    dayjs.extend(utc);
    dayjs.extend(timezone);
    dayjs.tz.setDefault('Europe/Zurich');

    return lang;
}

export function i18nInit(locale: string, localeStrings: LocaleStringMap) {
    i18nInstance = new VueI18n({
        // locale: process.env.VUE_APP_I18N_LOCALE || 'en',
        locale: locale || 'de',
        // fallbackLocale: process.env.VUE_APP_I18N_FALLBACK_LOCALE || 'en',
        messages: {
            [locale]: localeStrings,
        },
    });
    setI18nLanguage(i18nInstance, locale);
    return i18nInstance;
}
export function getI18nInstance(): VueI18n {
    if (!i18nInstance) {
        throw new Error('Not yet initialized. Use i18nInit() first.');
    }
    return i18nInstance;
}
